/**
 * PATH: packages/auth/src/authorityOrgStamp.ts
 * FILE_ID: AUTH-IDENTITY-ORGSTAMP-001
 * PURPOSE: Decide whether a persisted identity still describes the active org.
 * WHY: `display_email` / `authority_email` are resolved per MEMBERSHIP, so a
 *      cached user object is only true for the org it was fetched under. The
 *      boot path never noticed: checkAuth trusts any persisted user whose JWT
 *      subject matches, and neither refresh endpoint returns user fields. An
 *      org switch therefore survived a reload as the PREVIOUS org's address.
 *      Stamping the resolving org onto the user turns "is this cache still
 *      valid?" into a comparison instead of an assumption.
 * NOTES FOR FUTURE AI: an unstamped user is deliberately treated as stale, so
 *      identities persisted before this shipped self-heal on their next boot.
 *      Never widen this to also compare `organisation_id`: that field is set
 *      from the shell's resolved org, so it always matches and would make the
 *      check vacuously true.
 */

import type { User } from './types'

type Stamped = Pick<User, 'authority_org_id'>

/**
 * Should the profile be re-fetched before trusting this cached identity?
 *
 * Input:  the persisted user (or null), and the org the shell resolved.
 * Output: true only when we can prove the cache belongs to a different org.
 *
 * Returns false when there is no user (the caller already hydrates that case)
 * or no resolved org (nothing to compare against — churning a request there
 * would cost a round trip on every boot for no decidable gain).
 */
export function needsAuthorityRefetch(
  user: Stamped | null | undefined,
  resolvedOrgId: string | null | undefined,
): boolean {
  if (!user || !resolvedOrgId) return false
  return String(user.authority_org_id || '') !== String(resolvedOrgId)
}
